import { ReactNode, useEffect, useMemo, useState } from "react";

import { Callout, Divider } from "@blueprintjs/core";
import { differenceInMilliseconds } from "date-fns";
import intersection from "lodash/intersection";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { usePagination } from "@/components/helpers/custom-hooks/use-pagination";
import { formatMoney, pluralize } from "@/components/helpers/string-utils";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, BoxProps, Flex } from "@/components/layout/flexbox";
import { ErrorDialogContent } from "@/components/pages_logged_in/services/ErrorDialogContent";
import { SuccessDialogContent } from "@/components/pages_logged_in/services/SuccessDialogContent";
import {
  getConflictingLegacyPricingClients,
  PriceUpdateConfirmationStep,
} from "@/components/pages_logged_in/services/utils";
import { PrimaryButton } from "@/components/pieces/button/PrimaryButton";
import { PaginationButton } from "@/components/pieces/form/PaginationButton";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { SimpleTable } from "@/components/pieces/table/SimpleTable";
import { Avatar } from "@/components/pieces/users/Avatar";
import { getUser } from "@/components/session/JwtTokenManager";
import { INNER_BORDER_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import {
  ProductDetailQuery,
  SetProductPriceMutationVariables,
  useCoachCustomersQuery,
} from "@/graphql";
import { CoachClient } from "@/types/coaches";
import { CreditPriceOption } from "@/types/products";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";
import { formatCredits } from "@/utils/string";

import { DialogSection } from "./DialogSection";

function TwoRowBlock({
  row1,
  row2,
  ...boxProps
}: { row1: ReactNode; row2: ReactNode } & BoxProps) {
  return (
    <Flex flexDirection="column" alignItems="flex-end" {...boxProps}>
      <Box fontWeight={600}>{row1}</Box>
      <Box>{row2}</Box>
    </Flex>
  );
}

function PriceWithCreditIcon({ price }: { price: string }) {
  return (
    <Flex alignItems="center">
      <EmbedSvg src={CreditSVG} width={16} height={16}></EmbedSvg>
      <Box ml={2}>{formatCredits(price)}</Box>
    </Flex>
  );
}

interface SuccessTextProps {
  productName: string;
  specialtyName: string;
  newPriceText: string;
  legacyPricingEnabled: boolean;
  legacyPricingClientIds: string[];
}

function SuccessText({
  productName,
  specialtyName,
  newPriceText,
  legacyPricingEnabled,
  legacyPricingClientIds,
}: SuccessTextProps) {
  return (
    <>
      <div>
        Price of <b>{productName}</b> in the <b>{specialtyName}</b> specialty
        successfully set to <b>{newPriceText}</b>.
      </div>
      {legacyPricingEnabled && (
        <div>
          Legacy pricing enabled for{" "}
          <b>
            {pluralize({
              amount: legacyPricingClientIds.length,
              singular: "client",
              plural: "clients",
            })}
          </b>
          .
        </div>
      )}
    </>
  );
}

interface NoticeTextProps {
  legacyPricingEnabled: boolean;
  legacyPricingClientIds: string[];
  newPriceText: string;
  currentPriceText: string;
}

function NoticeText({
  legacyPricingEnabled,
  legacyPricingClientIds,
  newPriceText,
  currentPriceText,
}: NoticeTextProps) {
  return legacyPricingEnabled ? (
    <>
      <b>
        {pluralize({
          amount: legacyPricingClientIds.length,
          singular: "client",
          plural: "clients",
        })}{" "}
      </b>{" "}
      will be able to book this service for {currentPriceText}. All other
      clients will be offered your new price of {newPriceText}
    </>
  ) : (
    <>All clients will be offered your new price of {newPriceText}</>
  );
}

const COACH_CLIENTS_COLUMNS = [
  {
    id: "clients",
    header: <th align="left">Clients</th>,
    renderCell: (client: CoachClient) => {
      return (
        <td align="left">
          <Flex alignItems="center">
            <Tooltip content={client.email} placement="bottom">
              <Avatar user={client} size={20} />
            </Tooltip>
            <Box css={{ marginLeft: 6.75 }}>{client.name}</Box>
          </Flex>
        </td>
      );
    },
  },
  {
    id: "last-purchase",
    header: <th align="right">Last Purchase</th>,
    renderCell: (client: CoachClient) => {
      return (
        <td align="right">
          <Box textAlign="right" color={SUBTEXT_COLOR}>
            {client.lastPurchaseAt
              ? formatDate(client.lastPurchaseAt, "MMM d 'at' h:mma z")
              : "-"}
          </Box>
        </td>
      );
    },
  },
];

const PAGE_SIZE = 5;

interface PriceUpdateConfirmationDialogProps {
  coachProduct: ProductDetailQuery["productById"]["coachProduct"];
  product: ProductDetailQuery["productById"]["product"];
  newPriceOption?: CreditPriceOption;
  isOpen: boolean;
  isLoading: boolean;
  step: PriceUpdateConfirmationStep;
  onClose: () => void;
  onConfirm: (variables: SetProductPriceMutationVariables) => void;
}

export function PriceUpdateConfirmationDialog({
  coachProduct,
  product,
  newPriceOption,
  isOpen,
  isLoading,
  step,
  onClose,
  onConfirm,
}: PriceUpdateConfirmationDialogProps) {
  const [selectedLegacyCustomerIds, setSelectedLegacyCustomerIds] = useState<
    string[]
  >([]);

  const [suggestedClients, setSuggestedClients] = useState<string[]>([]);
  const currentUser = getUser();
  const coachId = currentUser?.id;
  const productId = coachProduct.productId;

  const { data, refetch: refetchCoachCustomers } = useCoachCustomersQuery({
    variables: {
      productId,
      includeCustomersOfOtherProducts: true,
      excludeWorkspaceCustomers: false,
    },
    skip: !productId,
  });

  const {
    coachClientsWithoutLegacyPricing,
    coachClientsWithoutLegacyPricingIds,
  } = useMemo(() => {
    if (!data) {
      return {
        coachClientsWithoutLegacyPricing: [],
        coachClientsWithoutLegacyPricingIds: [],
      };
    }
    // Filter the coach customers to only those without legacy pricing
    // and then sort through with lastPurchaseAt to the top
    const coachClientsWithoutLegacyPricing = ensureNonNullArray(
      data.coachCustomers,
    )
      .filter((client) => !client.currentPriceOption)
      .sort((a, b) => {
        if (!a.lastPurchaseAt) return 1;
        if (!b.lastPurchaseAt) return -1;
        return differenceInMilliseconds(
          new Date(b.lastPurchaseAt),
          new Date(a.lastPurchaseAt),
        );
      });
    return {
      coachClientsWithoutLegacyPricing,
      coachClientsWithoutLegacyPricingIds: coachClientsWithoutLegacyPricing.map(
        (client) => client.id,
      ),
    };
  }, [data]);

  const {
    startIndex,
    endIndex,
    totalNumberOfPages,
    back: backPage,
    next: nextPage,
  } = usePagination({
    pageSize: PAGE_SIZE,
    total: coachClientsWithoutLegacyPricing.length,
  });

  const displayedCoachClients = coachClientsWithoutLegacyPricing.slice(
    startIndex,
    endIndex + 1,
  );

  useEffect(() => {
    // reset the state of the modal
    if (isOpen && step === PriceUpdateConfirmationStep.INITIAL) {
      refetchCoachCustomers();
      setSelectedLegacyCustomerIds([]);

      const clientIds = getConflictingLegacyPricingClients(
        coachId || "",
        productId || "",
      );

      setSuggestedClients(clientIds);

      if (!clientIds.length) {
        handleSelectAllClients({ checked: true });
      }
    }
  }, [isOpen]);

  const currentPriceValue = coachProduct.coachPrice;
  const productName = product.name;
  const specialtyName = product.specialty?.name;

  const hasSelectedAnyUserForLegacyPricing =
    selectedLegacyCustomerIds.length > 0;

  const currentPriceText = formatCredits(currentPriceValue);
  const newPriceText = formatCredits(newPriceOption?.value);

  const shouldShowSuggestedClients =
    suggestedClients.length > 0 && coachClientsWithoutLegacyPricing.length > 0;

  const handleSelectClient = (row: { data: CoachClient; checked: boolean }) => {
    const { data, checked } = row;
    if (!checked) {
      setSelectedLegacyCustomerIds((prevIds) =>
        prevIds.filter((id) => id !== data.id),
      );
    } else {
      setSelectedLegacyCustomerIds((prevIds) => [...prevIds, row.data.id]);
    }
  };

  const handleSelectAllClients = ({ checked }: { checked: boolean }) => {
    setSelectedLegacyCustomerIds(
      checked ? coachClientsWithoutLegacyPricingIds : [],
    );
  };

  const isCheckedAllClients = useMemo(() => {
    return (
      coachClientsWithoutLegacyPricing.length > 0 &&
      coachClientsWithoutLegacyPricing.length ===
        selectedLegacyCustomerIds.length
    );
  }, [coachClientsWithoutLegacyPricing, selectedLegacyCustomerIds]);

  const selectSuggestedClients = () => {
    const actualSuggestedClients = intersection(
      suggestedClients,
      coachClientsWithoutLegacyPricingIds,
    );
    setSelectedLegacyCustomerIds(actualSuggestedClients);
    setSuggestedClients([]);
  };

  const _onConfirm = () => {
    onConfirm({
      productId: productId || "",
      price: formatMoney({ cashflow: newPriceOption?.value || "", unit: "" }),
      legacyCustomerIds: selectedLegacyCustomerIds,
    });
  };

  const mainContent = (
    <>
      <DialogSection title="Pending change">
        <Flex
          justifyContent="space-between"
          flexWrap="wrap"
          p={3}
          css={{ border: `1px solid ${INNER_BORDER_COLOR}`, borderRadius: 8 }}
        >
          <TwoRowBlock
            row1={productName}
            row2={specialtyName}
            alignItems="flex-start"
            flexBasis={["100%", "auto"]}
            pb={[3, 0]}
          />
          <TwoRowBlock
            row1="Previous Price"
            row2={<PriceWithCreditIcon price={currentPriceValue || "0.00"} />}
          />
          <TwoRowBlock
            row1="New Price"
            row2={
              newPriceOption && (
                <PriceWithCreditIcon price={newPriceOption.value} />
              )
            }
          />
        </Flex>
      </DialogSection>
      <DialogSection
        title="Legacy pricing"
        description="We recommend enabling legacy pricing your existing clients."
      >
        {shouldShowSuggestedClients && (
          <Clickable onClick={selectSuggestedClients}>
            <Box pb={12}>
              <Callout icon={null} style={{ backgroundColor: "#E9F1FB" }}>
                <Box fontWeight={700} color="#215DB0">
                  ⚡️ Click here to select the same{" "}
                  {pluralize({
                    amount: suggestedClients.length,
                    singular: "client",
                    plural: "clients",
                  })}{" "}
                  as last time
                </Box>
              </Callout>
            </Box>
          </Clickable>
        )}
        {isCheckedAllClients && (
          <Box pb={12}>
            <Callout icon={null} style={{ backgroundColor: "#E9F1FB" }}>
              <Box color="#215DB0">
                <b>
                  {pluralize({
                    amount: selectedLegacyCustomerIds.length,
                    singular: "user",
                    plural: "users",
                  })}
                </b>{" "}
                selected across{" "}
                <b>
                  {pluralize({
                    amount: totalNumberOfPages,
                    singular: "page",
                    plural: "pages",
                  })}
                </b>
              </Box>
            </Callout>
          </Box>
        )}
        <SimpleTable
          showCheckBox={coachClientsWithoutLegacyPricing.length > 0}
          columns={COACH_CLIENTS_COLUMNS}
          rows={displayedCoachClients}
          selectedIds={selectedLegacyCustomerIds}
          checkedAll={isCheckedAllClients}
          onCheckRow={handleSelectClient}
          onCheckAll={handleSelectAllClients}
          footerText={
            <Box fontWeight="normal" fontSize={13} textAlign="center">
              Clients who already have a custom price or legacy price are not
              shown here
            </Box>
          }
        />
        {coachClientsWithoutLegacyPricing.length > 0 && (
          <Flex justifyContent="center" alignItems="center" pt={12}>
            <PaginationButton
              icon="chevron-left"
              style={{ opacity: 0.3 }}
              onClick={backPage}
            />
            <Box mx={3}>
              {startIndex + 1} - {endIndex + 1} of{" "}
              {coachClientsWithoutLegacyPricing.length}
            </Box>
            <PaginationButton
              icon="chevron-right"
              style={{ opacity: 0.3 }}
              onClick={nextPage}
            />
          </Flex>
        )}
      </DialogSection>
      <Divider />
      <Box my={20}>
        <NoticeText
          legacyPricingEnabled={hasSelectedAnyUserForLegacyPricing}
          legacyPricingClientIds={selectedLegacyCustomerIds}
          newPriceText={newPriceText}
          currentPriceText={currentPriceText}
        />
      </Box>
      <Box mt={20}>
        <PrimaryButton loading={isLoading} onClick={_onConfirm}>
          Confirm
        </PrimaryButton>
      </Box>
    </>
  );

  const renderContent = () => {
    switch (step) {
      case PriceUpdateConfirmationStep.ERROR:
        return <ErrorDialogContent onClose={onClose} />;
      case PriceUpdateConfirmationStep.SUCCESS:
        return (
          <SuccessDialogContent onClose={onClose}>
            <SuccessText
              productName={productName || "Unknown Product"}
              specialtyName={specialtyName || "Unknown Specialty"}
              newPriceText={newPriceText}
              legacyPricingEnabled={hasSelectedAnyUserForLegacyPricing}
              legacyPricingClientIds={selectedLegacyCustomerIds}
            />
          </SuccessDialogContent>
        );
      case PriceUpdateConfirmationStep.INITIAL:
      default:
        return mainContent;
    }
  };

  return (
    <StyledDialog
      lazy
      usePortal
      isOpen={isOpen}
      width={step === PriceUpdateConfirmationStep.INITIAL ? "650px" : "480px"}
      title="Price Update"
      onClose={onClose}
    >
      <Box p={24}>{renderContent()}</Box>
    </StyledDialog>
  );
}
